import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Calendar, Plus, Users, Trash2, MapPin, Pencil, Building2, Paperclip, FileText, X, Upload } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AddressLink, PhoneLink, EmailLink } from "@/components/marketing/contact-links";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const emptyForm = { event_name: "", contact_lead_id: "", organization_name: "", contact_name: "",
  phone: "", email: "", event_address: "", date: format(new Date(), "yyyy-MM-dd"),
  setup_time: "", time: "", end_time: "", expected_headcount: "", event_details: "", event_outcome: "", attachments: [] };

function Attachments({ items, onAdd, onRemove }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onAdd(data);
      toast.success("File attached");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); ev.target.value = ""; }
  };
  return (
    <div className="space-y-2">
      <Label>Attachments</Label>
      {(items || []).map((a) => (
        <div key={a.path} className="flex items-center justify-between border px-3 py-2 rounded-md bg-muted/40">
          <a href={`${BACKEND_URL}${a.url}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline truncate">
            <FileText className="h-4 w-4 shrink-0" /><span className="truncate">{a.name}</span>
          </a>
          {onRemove && <button type="button" onClick={() => onRemove(a.path)} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>}
        </div>
      ))}
      {onRemove && (
        <label className="flex items-center justify-center gap-2 border border-dashed rounded-md px-3 py-3 text-sm text-muted-foreground cursor-pointer hover:bg-muted/40">
          {uploading ? <><Upload className="h-4 w-4 animate-pulse" /> Uploading…</> : <><Paperclip className="h-4 w-4" /> Attach file (flyer, photo, PDF…)</>}
          <input data-testid="event-file-input" type="file" className="hidden" onChange={upload} disabled={uploading} />
        </label>
      )}
    </div>
  );
}

function EventForm({ form, setForm, leads, onSubmit, onCancel, submitLabel }) {
  const pickContact = (id) => {
    const l = leads.find((x) => x.id === id);
    setForm((f) => ({ ...f, contact_lead_id: id,
      organization_name: l?.business_name || f.organization_name,
      contact_name: l?.contact_person || f.contact_name,
      phone: l?.phone || f.phone, email: l?.email || f.email }));
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <form onSubmit={onSubmit} className="space-y-4 pt-2">
      <div className="space-y-2"><Label>Event Name *</Label>
        <Input value={form.event_name} data-testid="event-name" onChange={set("event_name")} placeholder="e.g., Back to School Bash" required /></div>
      <div className="space-y-2"><Label>Contact (from Targets)</Label>
        <Select value={form.contact_lead_id} onValueChange={pickContact}>
          <SelectTrigger data-testid="event-contact"><SelectValue placeholder="Select a contact (optional)" /></SelectTrigger>
          <SelectContent>{leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.business_name} — {l.contact_person}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-2"><Label>Organization Name *</Label>
        <Input value={form.organization_name} data-testid="event-org" onChange={set("organization_name")} required /></div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Contact Person</Label><Input value={form.contact_name} onChange={set("contact_name")} /></div>
        <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} /></div>
        <div className="space-y-2"><Label>Event Address</Label><Input value={form.event_address} onChange={set("event_address")} /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Date *</Label><Input type="date" value={form.date} data-testid="event-date" onChange={set("date")} required /></div>
        <div className="space-y-2"><Label>Expected Headcount</Label><Input type="number" value={form.expected_headcount} onChange={set("expected_headcount")} placeholder="50" /></div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-2"><Label>Setup</Label><Input type="time" value={form.setup_time} onChange={set("setup_time")} /></div>
        <div className="space-y-2"><Label>Start</Label><Input type="time" value={form.time} onChange={set("time")} /></div>
        <div className="space-y-2"><Label>End</Label><Input type="time" value={form.end_time} onChange={set("end_time")} /></div>
      </div>
      <div className="space-y-2"><Label>Event Details</Label>
        <Textarea value={form.event_details} onChange={set("event_details")} rows={3} placeholder="Setup instructions, booth requirements, logistics..." /></div>
      <div className="space-y-2"><Label>Event Outcome / Recap</Label>
        <Textarea value={form.event_outcome} onChange={set("event_outcome")} rows={2} placeholder="Results, notes..." /></div>
      <Attachments items={form.attachments}
        onAdd={(a) => setForm((f) => ({ ...f, attachments: [...(f.attachments || []), a] }))}
        onRemove={(path) => setForm((f) => ({ ...f, attachments: (f.attachments || []).filter((x) => x.path !== path) }))} />
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" data-testid="save-event" disabled={!form.event_name.trim() || !form.organization_name.trim()}>{submitLabel}</Button>
      </DialogFooter>
    </form>
  );
}

export function EventsModule({ events, onAddEvent, onUpdateEvent, onDeleteEvent, leads = [] }) {
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState(null);

  const openAdd = () => { setForm(emptyForm); setAddOpen(true); };
  const openEdit = (ev) => { setForm({ ...emptyForm, ...ev, contact_lead_id: "", attachments: ev.attachments || [] }); setSelected(ev); setEditOpen(true); };

  const toPayload = (f) => ({
    event_name: f.event_name.trim(), organization_name: f.organization_name.trim(),
    contact_name: f.contact_name, phone: f.phone, email: f.email, event_address: f.event_address,
    date: f.date, time: f.time, setup_time: f.setup_time, end_time: f.end_time,
    expected_headcount: parseInt(f.expected_headcount, 10) || 0,
    event_details: f.event_details, event_outcome: f.event_outcome, attachments: f.attachments || [],
  });

  const submitAdd = async (e) => { e.preventDefault(); await onAddEvent(toPayload(form)); setAddOpen(false); };
  const submitEdit = async (e) => { e.preventDefault(); await onUpdateEvent(selected.id, toPayload(form)); setEditOpen(false); setDetailOpen(false); };

  const eventContacts = leads.filter((l) => l.is_event);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const upcoming = events.filter((e) => new Date(e.date) >= today).length;

  return (
    <Card className="card-shadow border-0">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-bold flex items-center gap-2"><Calendar className="h-5 w-5 text-primary" /> Events</CardTitle>
          <Button size="sm" className="gap-1" data-testid="add-event-button" onClick={openAdd}><Plus className="h-4 w-4" /> Add Event</Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="events">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="events" data-testid="events-list-tab">Events List{upcoming > 0 && <span className="ml-2 bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">{upcoming}</span>}</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="events-contacts-tab">Contacts</TabsTrigger>
          </TabsList>
          <TabsContent value="events">
            {events.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground"><Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>No events scheduled</p><p className="text-sm mt-1">Click "Add Event" to schedule one</p></div>
            ) : (
              <div className="space-y-3 max-h-[420px] overflow-y-auto" data-testid="events-list">
                {events.map((ev) => (
                  <div key={ev.id} data-testid={`event-${ev.id}`} className="p-4 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors cursor-pointer" onClick={() => { setSelected(ev); setDetailOpen(true); }}>
                    <div className="flex items-start justify-between">
                      <div><h4 className="font-medium">{ev.event_name}</h4><p className="text-sm text-muted-foreground">{ev.organization_name}{ev.contact_name ? ` • ${ev.contact_name}` : ""}</p></div>
                      <div className="flex items-center gap-2">
                        {ev.expected_headcount > 0 && <Badge variant="outline" className="flex items-center gap-1"><Users className="h-3 w-3" />{ev.expected_headcount}</Badge>}
                        {(ev.attachments || []).length > 0 && <Badge variant="outline" className="flex items-center gap-1"><Paperclip className="h-3 w-3" />{ev.attachments.length}</Badge>}
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`delete-event-${ev.id}`} onClick={(e) => { e.stopPropagation(); onDeleteEvent(ev.id); }}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">{ev.date}{ev.time && <span className="ml-2">• {ev.time}{ev.end_time ? `–${ev.end_time}` : ""}</span>}</div>
                    {ev.event_address && <div className="mt-1 text-sm flex items-center gap-1"><MapPin className="h-3 w-3 text-muted-foreground" /><AddressLink address={ev.event_address} className="text-sm" /></div>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="contacts">
            {eventContacts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground"><Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>No event contacts yet</p><p className="text-sm">Tag a contact as "Event" in the Targets tab.</p></div>
            ) : (
              <div className="space-y-3 max-h-[420px] overflow-y-auto">
                {eventContacts.map((lead) => {
                  const count = events.filter((e) => e.contact_name === lead.contact_person || e.organization_name === lead.business_name).length;
                  return (
                    <div key={lead.id} className="p-4 rounded-lg border bg-card">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1"><Building2 className="h-4 w-4 text-primary" /><h4 className="font-semibold">{lead.business_name}</h4></div>
                          <p className="text-sm text-muted-foreground mb-2">{lead.contact_person}</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                            <PhoneLink phone={lead.phone} className="text-xs" /><EmailLink email={lead.email} className="text-xs" />
                            <div className="sm:col-span-2"><AddressLink address={lead.address} className="text-xs" /></div>
                          </div>
                        </div>
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full font-medium ml-4">{count} events</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add New Event</DialogTitle><DialogDescription>Schedule an event and attach flyers or photos.</DialogDescription></DialogHeader>
          <EventForm form={form} setForm={setForm} leads={leads} onSubmit={submitAdd} onCancel={() => setAddOpen(false)} submitLabel="Save Event" />
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Event</DialogTitle><DialogDescription>Update event details and attachments.</DialogDescription></DialogHeader>
          <EventForm form={form} setForm={setForm} leads={leads} onSubmit={submitEdit} onCancel={() => setEditOpen(false)} submitLabel="Save Changes" />
        </DialogContent>
      </Dialog>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader className="relative pr-10">
            <DialogTitle>Event Details</DialogTitle>
            <Button variant="ghost" size="icon" data-testid="edit-event-button" onClick={() => { setDetailOpen(false); openEdit(selected); }} className="absolute right-0 top-0 h-8 w-8"><Pencil className="h-4 w-4" /></Button>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 pt-2 text-sm">
              <div><Label className="text-muted-foreground">Event Name</Label><p className="font-medium text-lg">{selected.event_name}</p></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-muted-foreground">Organization</Label><p className="font-medium">{selected.organization_name}</p></div>
                <div><Label className="text-muted-foreground">Contact</Label><p className="font-medium">{selected.contact_name || "—"}</p></div>
              </div>
              {(selected.phone || selected.email) && <div className="grid grid-cols-2 gap-4">{selected.phone && <PhoneLink phone={selected.phone} />}{selected.email && <EmailLink email={selected.email} />}</div>}
              {selected.event_address && <div className="flex items-center gap-1"><MapPin className="h-4 w-4 text-muted-foreground" /><AddressLink address={selected.event_address} /></div>}
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-muted-foreground">Date</Label><p className="font-medium">{selected.date}</p></div>
                <div><Label className="text-muted-foreground">Headcount</Label><p className="font-medium">{selected.expected_headcount || "—"}</p></div>
              </div>
              {(selected.setup_time || selected.time || selected.end_time) && <div><Label className="text-muted-foreground">Times</Label><p className="font-medium">{selected.setup_time && `Setup ${selected.setup_time} · `}{selected.time}{selected.end_time ? `–${selected.end_time}` : ""}</p></div>}
              {selected.event_details && <div><Label className="text-muted-foreground">Details</Label><p className="mt-1 p-3 bg-secondary rounded-md whitespace-pre-wrap">{selected.event_details}</p></div>}
              {selected.event_outcome && <div><Label className="text-muted-foreground">Outcome</Label><p className="mt-1 p-3 bg-secondary rounded-md whitespace-pre-wrap">{selected.event_outcome}</p></div>}
              {(selected.attachments || []).length > 0 && <Attachments items={selected.attachments} />}
              <div className="flex justify-end"><Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
